import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import PreferenceTable from '../components/PreferenceTable';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { getStatusLabel } from '../lib/proposalUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Search,
  Phone, 
  Mail,
  MapPin,
  Building,
  Edit,
  Trash2,
  Loader2,
  Globe,
  User,
  ChevronRight,
  Briefcase,
  X,
  Copy,
  Calendar,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Wrench,
  AlertTriangle,
  FileText,
  LayoutGrid,
  TableIcon,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Target
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
];

const TITLE_OPTIONS = ['Owner', 'Manager', 'Project Manager', 'Superintendent', 'Estimator', 'Engineer', 'Foreman', 'Admin', 'Accounts Payable', 'Receptionist', 'Other'];

const emptyContact = {
  first_name: '',
  last_name: '',
  name: '',
  title: '',
  email: '',
  phone: ''
};

const emptyForm = {
  client_name: '',
  website: '',
  industry_classification: '',
  custom_industry: '',
  // Physical Address
  physical_street: '',
  physical_city: '',
  physical_state: '',
  physical_zip: '',
  // Billing Address
  billing_street: '',
  billing_city: '',
  billing_state: '',
  billing_zip: '',
  // AP Email Address
  ar_email_address: '',
  // Contacts array
  contacts: [],
  notes: ''
};

export default function Contacts() {
  const { user, isAdmin, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [industryClassifications, setIndustryClassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientJobs, setClientJobs] = useState([]);
  const [clientProposals, setClientProposals] = useState([]);
  const [clientOpportunities, setClientOpportunities] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  
  // Cleanup state
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);
  const [runningCleanup, setRunningCleanup] = useState(false);
  
  // View mode: 'table' or 'cards' - default to table, persist in localStorage
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('companiesViewMode') || 'table';
  });
  
  // Sorting state
  const [sortBy, setSortBy] = useState('company_name');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Update localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('companiesViewMode', viewMode);
  }, [viewMode]);

  const canManage = isAdmin() || hasPermission('manage_contacts');
  const canCreateProposals = isAdmin() || hasPermission('create_proposals');

  // Navigate to proposals page with company data pre-filled
  const handleNewProposal = (client, contact = null) => {
    const params = new URLSearchParams({ new: 'true' });
    
    // Add company ID and name
    params.append('companyId', client.id || '');
    params.append('company', client.company_name || client.client_name || '');
    
    // Only use BILLING address — leave blank if not set (no fallback to physical)
    if (client.billing_street) params.append('street', client.billing_street);
    if (client.billing_city) params.append('city', client.billing_city);
    if (client.billing_state) params.append('state', client.billing_state);
    if (client.billing_zip) params.append('zip', client.billing_zip);
    
    // Add AP Email (accounts payable)
    if (client.ar_email_address) {
      params.append('apEmail', client.ar_email_address);
    }
    
    // Add contact info if provided
    if (contact) {
      const contactName = contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
      params.append('contact', contactName);
      params.append('email', contact.email || '');
      params.append('phone', contact.phone || contact.phone1 || '');
    }
    
    navigate(`/proposals?${params.toString()}`);
  };

  const fetchClients = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/contacts${params}`);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchIndustryClassifications = async () => {
    try {
      const response = await axios.get(`${API}/industry-classifications`);
      setIndustryClassifications(response.data);
    } catch (error) {
      console.error('Failed to fetch industry classifications:', error);
    }
  };

  useEffect(() => {
    fetchClients();
    fetchIndustryClassifications();
  }, [fetchClients]);

  const fetchClientJobs = async (clientId) => {
    try {
      const response = await axios.get(`${API}/contacts/${clientId}/jobs`);
      setClientJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      setClientJobs([]);
    }
  };

  const fetchClientProposals = async (clientId) => {
    try {
      const response = await axios.get(`${API}/contacts/${clientId}/proposals`);
      setClientProposals(response.data);
    } catch (error) {
      console.error('Failed to fetch proposals:', error);
      setClientProposals([]);
    }
  };

  const fetchClientOpportunities = async (clientId) => {
    try {
      const response = await axios.get(`${API}/contacts/${clientId}/opportunities`);
      setClientOpportunities(response.data);
    } catch (error) {
      console.error('Failed to fetch opportunities:', error);
      setClientOpportunities([]);
    }
  };

  const handleAddNew = () => {
    setSelectedClient(null);
    setFormData(emptyForm);
    setShowDialog(true);
  };

  const handleEdit = async (client) => {
    setSelectedClient(client);
    
    // Fetch associated people from the People collection
    let contactsFromPeople = [];
    try {
      const response = await axios.get(`${API}/people?company_id=${client.id}`);
      contactsFromPeople = response.data.map(person => ({
        id: person.id,  // Keep the person ID for updates
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        name: person.full_name || `${person.first_name || ''} ${person.last_name || ''}`.trim(),
        title: person.job_title || '',
        email: person.primary_email || (person.emails?.[0]?.email) || '',
        phone: person.primary_phone || (person.phones?.[0]?.number) || ''
      }));
    } catch (error) {
      console.error('Failed to fetch people for company:', error);
    }
    
    // Use contacts from People collection, fall back to nested contacts array
    const contacts = contactsFromPeople.length > 0 
      ? contactsFromPeople 
      : (client.contacts || client.people || []).map(c => ({
          first_name: c.first_name || c.name?.split(' ')[0] || '',
          last_name: c.last_name || c.name?.split(' ').slice(1).join(' ') || '',
          name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
          title: c.title || c.role || '',
          email: c.email || '',
          phone: c.phone || c.phone1 || ''
        }));
    
    setFormData({
      client_name: client.company_name || client.client_name || '',
      website: client.website || '',
      industry_classification: client.industry_classification || '',
      physical_street: client.physical_street || client.street || '',
      physical_city: client.physical_city || client.city || '',
      physical_state: client.physical_state || client.state || '',
      physical_zip: client.physical_zip || client.zip_code || '',
      billing_street: client.billing_street || '',
      billing_city: client.billing_city || '',
      billing_state: client.billing_state || '',
      billing_zip: client.billing_zip || '',
      ar_email_address: client.ar_email_address || '',
      contacts: contacts,
      notes: client.notes || ''
    });
    setShowDialog(true);
  };

  const handleDuplicate = async (client) => {
    setSelectedClient(null);
    
    // Fetch associated people from the People collection
    let contactsFromPeople = [];
    try {
      const response = await axios.get(`${API}/people?company_id=${client.id}`);
      contactsFromPeople = response.data.map(person => ({
        // Don't include ID so new people are created for the duplicate
        first_name: person.first_name || '',
        last_name: person.last_name || '',
        name: person.full_name || `${person.first_name || ''} ${person.last_name || ''}`.trim(),
        title: person.job_title || '',
        email: person.primary_email || (person.emails?.[0]?.email) || '',
        phone: person.primary_phone || (person.phones?.[0]?.number) || ''
      }));
    } catch (error) {
      console.error('Failed to fetch people for company:', error);
    }
    
    const contacts = contactsFromPeople.length > 0 
      ? contactsFromPeople 
      : (client.contacts || client.people || []).map(c => ({
          first_name: c.first_name || c.name?.split(' ')[0] || '',
          last_name: c.last_name || c.name?.split(' ').slice(1).join(' ') || '',
          name: c.name || '',
          title: c.title || c.role || '',
          email: c.email || '',
          phone: c.phone || c.phone1 || ''
        }));
    
    setFormData({
      client_name: `${client.company_name || client.client_name || ''} (Copy)`,
      website: client.website || '',
      industry_classification: client.industry_classification || '',
      physical_street: client.physical_street || client.street || '',
      physical_city: client.physical_city || client.city || '',
      physical_state: client.physical_state || client.state || '',
      physical_zip: client.physical_zip || client.zip_code || '',
      billing_street: client.billing_street || '',
      billing_city: client.billing_city || '',
      billing_state: client.billing_state || '',
      billing_zip: client.billing_zip || '',
      ar_email_address: client.ar_email_address || '',
      contacts: contacts,
      notes: client.notes || ''
    });
    setShowDetailView(false);
    setShowDialog(true);
    toast.info('Duplicating client - make your changes and save');
  };

  const handleViewDetail = async (client) => {
    try {
      // Fetch full client details including linked people
      const response = await axios.get(`${API}/contacts/${client.id}`);
      setSelectedClient(response.data);
      // Fetch related records in parallel
      await Promise.all([
        fetchClientJobs(client.id),
        fetchClientProposals(client.id),
        fetchClientOpportunities(client.id)
      ]);
      setShowDetailView(true);
    } catch (error) {
      console.error('Failed to fetch client details:', error);
      // Fallback to the list data
      setSelectedClient(client);
      await Promise.all([
        fetchClientJobs(client.id),
        fetchClientProposals(client.id),
        fetchClientOpportunities(client.id)
      ]);
      setShowDetailView(true);
    }
  };

  // Navigate to People/Contacts page to create a new contact for this company
  const handleCreateContact = (client) => {
    // Navigate to people (contacts) page with company pre-filled
    const params = new URLSearchParams({ 
      new: 'true',
      companyId: client.id,
      companyName: client.company_name || client.client_name || ''
    });
    navigate(`/people?${params.toString()}`);
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { ...emptyContact }]
    }));
  };

  const handleRemoveContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const handleContactChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.map((c, i) => i === index ? { ...c, [field]: value } : c)
    }));
  };

  const copyBillingFromPhysical = () => {
    setFormData(prev => ({
      ...prev,
      billing_street: prev.physical_street,
      billing_city: prev.physical_city,
      billing_state: prev.physical_state,
      billing_zip: prev.physical_zip
    }));
    toast.success('Billing address copied from physical address');
  };

  const handleSave = async () => {
    if (!formData.client_name) {
      toast.error('Client name is required');
      return;
    }

    // Map to backend expected format - DO NOT include nested contacts
    // Contacts will be stored in the People collection only to avoid duplicates
    const payload = {
      company_name: formData.client_name,
      website: formData.website?.trim() || null,
      industry_classification: formData.industry_classification || null,
      // Physical address
      physical_street: formData.physical_street?.trim() || null,
      physical_city: formData.physical_city?.trim() || null,
      physical_state: formData.physical_state || null,
      physical_zip: formData.physical_zip?.trim() || null,
      // Keep legacy fields for backward compatibility
      street: formData.physical_street?.trim() || null,
      city: formData.physical_city?.trim() || null,
      state: formData.physical_state || null,
      zip_code: formData.physical_zip?.trim() || null,
      // Billing address
      billing_street: formData.billing_street?.trim() || null,
      billing_city: formData.billing_city?.trim() || null,
      billing_state: formData.billing_state || null,
      billing_zip: formData.billing_zip?.trim() || null,
      // AP Email Address for billing
      ar_email_address: formData.ar_email_address?.trim() || null,
      // DO NOT include contacts here - they go to People collection only
      contacts: [],
      notes: formData.notes?.trim() || null,
      // Audit fields
      entered_by: user?.full_name || 'Unknown',
      entered_by_id: user?.id || null
    };

    setSaving(true);
    try {
      let companyId;
      if (selectedClient) {
        await axios.put(`${API}/contacts/${selectedClient.id}`, payload);
        companyId = selectedClient.id;
        toast.success('Client updated');
      } else {
        const response = await axios.post(`${API}/contacts`, payload);
        companyId = response.data.id;
        toast.success('Client created');
      }

      // Now sync contacts to People collection
      const contactsToSync = formData.contacts.filter(c => 
        (c.first_name?.trim() || c.last_name?.trim() || c.name?.trim())
      );

      for (const contact of contactsToSync) {
        const firstName = contact.first_name || contact.name?.split(' ')[0] || '';
        const lastName = contact.last_name || contact.name?.split(' ').slice(1).join(' ') || '';
        
        try {
          if (contact.id) {
            // Update existing person
            await axios.put(`${API}/people/${contact.id}`, {
              first_name: firstName,
              last_name: lastName,
              company_id: companyId,
              company_name: formData.client_name,
              job_title: contact.title || null,
              phones: contact.phone ? [{ number: contact.phone, type: 'work', is_primary: true }] : [],
              emails: contact.email ? [{ email: contact.email, type: 'work', is_primary: true }] : [],
            });
          } else {
            // Check if this person already exists in People collection
            const searchName = `${firstName} ${lastName}`.trim();
            const existingPeople = await axios.get(`${API}/people?search=${encodeURIComponent(searchName)}`);
            const existingPerson = existingPeople.data.find(p => 
              p.first_name?.toLowerCase() === firstName?.toLowerCase() && 
              p.last_name?.toLowerCase() === lastName?.toLowerCase() &&
              p.company_id === companyId
            );

            if (existingPerson) {
              // Update existing person
              await axios.put(`${API}/people/${existingPerson.id}`, {
                first_name: firstName,
                last_name: lastName,
                company_id: companyId,
                company_name: formData.client_name,
                job_title: contact.title || null,
                phones: contact.phone ? [{ number: contact.phone, type: 'work', is_primary: true }] : [],
                emails: contact.email ? [{ email: contact.email, type: 'work', is_primary: true }] : [],
              });
            } else if (firstName || lastName) {
              // Create new person linked to this company
              await axios.post(`${API}/people`, {
                first_name: firstName,
                last_name: lastName,
                company_id: companyId,
                company_name: formData.client_name,
                job_title: contact.title || null,
                phones: contact.phone ? [{ number: contact.phone, type: 'work', is_primary: true }] : [],
                emails: contact.email ? [{ email: contact.email, type: 'work', is_primary: true }] : [],
                addresses: []
              });
            }
          }
        } catch (err) {
          console.error('Failed to sync contact to People collection:', err);
        }
      }

      setShowDialog(false);
      fetchClients();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const messages = detail.map(err => err.msg || 'Validation error').join(', ');
        toast.error(messages);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save client');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/contacts/${selectedClient.id}`);
      toast.success('Client deleted');
      setShowDeleteDialog(false);
      setShowDetailView(false);
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    } finally {
      setSaving(false);
    }
  };

  // Import Functions
  const handleImportClick = () => {
    setImportResult(null);
    setShowImportDialog(true);
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/contacts/import/csv`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} contacts!`);
        fetchClients();
      } else {
        toast.warning('No contacts were imported');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Import failed';
      toast.error(message);
      setImportResult({ success: false, error: message });
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadTemplate = () => {
    // Open the backend endpoint directly - it will trigger a file download
    window.open(`${API}/contacts/import/template`, '_blank');
    toast.success('Template download started!');
  };

  // Cleanup duplicate contacts functions
  const runCleanupDryRun = async () => {
    setRunningCleanup(true);
    setCleanupResult(null);
    try {
      const response = await axios.post(`${API}/contacts/cleanup/duplicate-contacts?dry_run=true`);
      setCleanupResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run cleanup preview');
    } finally {
      setRunningCleanup(false);
    }
  };

  const runCleanupActual = async () => {
    setRunningCleanup(true);
    try {
      const response = await axios.post(`${API}/contacts/cleanup/duplicate-contacts?dry_run=false`);
      setCleanupResult(response.data);
      toast.success(`Cleanup complete! Removed duplicates from ${response.data.cleaned} companies.`);
      fetchClients(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setRunningCleanup(false);
    }
  };

  const formatAddress = (street, city, state, zip) => {
    const parts = [];
    if (street) parts.push(street);
    const cityStateZip = [city, state, zip].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join(', ');
  };

  const getContactsCount = (client) => {
    // Use pre-computed count from backend if available
    if (client.people_count !== undefined) {
      return client.people_count;
    }
    // Fallback to calculating from loaded data
    const oldContacts = client.contacts?.length || 0;
    const newPeople = client.people?.length || 0;
    return oldContacts + newPeople;
  };

  const getDisplayContacts = (client) => {
    const allContacts = [];
    
    // Add old nested contacts (if any)
    if (client.contacts && client.contacts.length > 0) {
      allContacts.push(...client.contacts.map(c => ({
        id: c.id,
        name: c.name || '',
        title: c.title || '',
        email: c.email || '',
        phone: c.phone || c.mobile || '',
        source: 'nested'
      })));
    }
    
    // Add new people from people collection (if any)
    if (client.people && client.people.length > 0) {
      allContacts.push(...client.people.map(p => ({
        id: p.id,
        name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
        title: p.job_title || p.role || '',
        email: p.emails?.[0]?.email || p.email || '',
        phone: p.phones?.[0]?.number || p.phone1 || '',
        source: 'people'
      })));
    }
    
    return allContacts;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-purple-100 text-purple-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  // Sort function for companies
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Sort icon component
  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-30" />;
    return sortOrder === 'asc' 
      ? <ChevronUp className="w-4 h-4 ml-1" /> 
      : <ChevronDown className="w-4 h-4 ml-1" />;
  };

  // Sorted clients
  const sortedClients = [...clients].sort((a, b) => {
    let aVal = a[sortBy] || a.company_name || a.client_name || '';
    let bVal = b[sortBy] || b.company_name || b.client_name || '';
    
    // Handle company name specifically
    if (sortBy === 'company_name') {
      aVal = a.company_name || a.client_name || '';
      bVal = b.company_name || b.client_name || '';
    }
    
    // Handle contacts count
    if (sortBy === 'contacts_count') {
      aVal = getContactsCount(a);
      bVal = getContactsCount(b);
    }
    
    // Handle strings
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Column config for PreferenceTable — mirrors the previous hard-coded columns.
  const contactColumns = [
    { id: 'company_name', label: 'Company', sortKey: 'company_name', defaultWidth: 260,
      render: (client) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Building className="w-4 h-4 text-amber-600" />
          </div>
          <span className="font-medium text-slate-900 truncate">{client.company_name || client.client_name}</span>
        </div>
      ) },
    { id: 'contacts_count', label: 'Contacts', sortKey: 'contacts_count', defaultWidth: 110,
      render: (client) => {
        const n = getContactsCount(client);
        return <span className="text-slate-600">{n} {n === 1 ? 'contact' : 'contacts'}</span>;
      } },
    { id: 'address', label: 'Address', defaultWidth: 260,
      render: (client) => {
        const physicalAddress = formatAddress(
          client.physical_street || client.street,
          client.physical_city || client.city,
          client.physical_state || client.state,
          client.physical_zip || client.zip_code
        );
        return physicalAddress ? (
          <div className="flex items-center gap-1 text-slate-500 text-sm">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{physicalAddress}</span>
          </div>
        ) : <span className="text-slate-400 text-sm">—</span>;
      } },
    { id: 'website', label: 'Website', defaultWidth: 200,
      render: (client) => client.website ? (
        <a
          href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
          target="_blank" rel="noopener noreferrer"
          className="text-amber-600 hover:underline text-sm truncate block"
          onClick={(e) => e.stopPropagation()}
        >
          {client.website.replace(/^https?:\/\//, '')}
        </a>
      ) : <span className="text-slate-400 text-sm">—</span> },
    { id: 'actions', label: 'Actions', defaultWidth: 120,
      render: (client) => (
        <div className="flex items-center gap-1">
          {canCreateProposals && (
            <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              onClick={(e) => { e.stopPropagation(); handleNewProposal(client); }} title="New Proposal">
              <FileText className="w-4 h-4" />
            </Button>
          )}
          {canManage && (
            <Button variant="ghost" size="sm"
              onClick={(e) => { e.stopPropagation(); handleEdit(client); }} title="Edit">
              <Edit className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) },
  ];
  const contactsPrefs = useTablePreferences('contacts', contactColumns);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-40 bg-slate-200 animate-pulse rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            CRM / Clients
          </h1>
          <p className="text-slate-500 mt-1">Manage clients and their contacts</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('table')}
              className={viewMode === 'table' ? 'bg-slate-800 text-white' : ''}
            >
              <TableIcon className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'cards' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('cards')}
              className={viewMode === 'cards' ? 'bg-slate-800 text-white' : ''}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
          {canManage && (
            <>
              {isAdmin() && (
                <Button 
                  variant="outline"
                  className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  onClick={() => { setShowCleanupDialog(true); setCleanupResult(null); }}
                  data-testid="cleanup-contacts-btn"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Fix Duplicates
                </Button>
              )}
              <Button 
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={handleImportClick}
                data-testid="import-csv-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button 
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                onClick={handleAddNew}
                data-testid="add-client-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Client
              </Button>
            </>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search clients, contacts, emails..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-slate-50 border-slate-200"
              data-testid="client-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {clients.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Building className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No clients found</h3>
            <p className="text-slate-500">
              {search ? 'Try a different search term' : 'Add your first client to get started'}
            </p>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        /* Table View — resize, reorder, hide columns are per-user prefs (Phase 1) */
        <Card className="border-slate-200">
          <div className="p-3">
            <PreferenceTable
              columns={contactColumns}
              rows={sortedClients}
              rowKey={(c) => c.id}
              prefs={contactsPrefs}
              sortBy={sortBy}
              sortDir={sortOrder}
              onSort={(col) => handleSort(col.sortKey)}
              onRowClick={(client) => handleViewDetail(client)}
              emptyMessage="No companies match your filters."
              rowClassName={() => 'cursor-pointer hover:bg-slate-50'}
              data-testid="contacts-table"
            />
          </div>
        </Card>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedClients.map(client => {
            const contactsCount = getContactsCount(client);
            const physicalAddress = formatAddress(
              client.physical_street || client.street,
              client.physical_city || client.city,
              client.physical_state || client.state,
              client.physical_zip || client.zip_code
            );
            return (
              <Card 
                key={client.id} 
                className="border-slate-200 hover:border-amber-500/50 cursor-pointer transition-all"
                onClick={() => handleViewDetail(client)}
                data-testid={`client-card-${client.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{client.company_name || client.client_name}</h3>
                        <p className="text-sm text-slate-500">
                          {contactsCount} {contactsCount === 1 ? 'contact' : 'contacts'}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>

                  {physicalAddress && (
                    <div className="flex items-start gap-2 mt-3 text-sm text-slate-500">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{physicalAddress}</span>
                    </div>
                  )}

                  {client.website && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                      <Globe className="w-4 h-4" />
                      <span className="truncate">{client.website}</span>
                    </div>
                  )}

                  {client.entered_by && (
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <User className="w-3 h-3" />
                      <span>Added by {client.entered_by}</span>
                    </div>
                  )}

                  {/* New Proposal Button - only for users with create_proposals permission */}
                  {canCreateProposals && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNewProposal(client);
                      }}
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      New Proposal
                    </Button>
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Client Detail View */}
      <Dialog open={showDetailView} onOpenChange={setShowDetailView}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedClient && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl sm:text-2xl font-bold break-words pr-8">{selectedClient.company_name || selectedClient.client_name}</DialogTitle>
              </DialogHeader>
              
              {/* Action buttons - separated from header with spacing */}
              <div className="flex gap-2 flex-wrap border-b pb-4 mb-2">
                {canCreateProposals && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    onClick={() => handleNewProposal(selectedClient)}
                  >
                    <FileText className="w-4 h-4 mr-1" />New Proposal
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handleDuplicate(selectedClient)} title="Duplicate">
                      <Copy className="w-4 h-4 mr-1" />Duplicate
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowDetailView(false); handleEdit(selectedClient); }}>
                      <Edit className="w-4 h-4 mr-1" />Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setShowDeleteDialog(true)}>
                      <Trash2 className="w-4 h-4 mr-1" />Delete
                    </Button>
                  </>
                )}
              </div>

              <div className="space-y-4 sm:space-y-6">
                {/* Addresses */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4 flex-shrink-0" />Physical Address
                    </h4>
                    <p className="text-sm break-words">
                      {formatAddress(
                        selectedClient.physical_street || selectedClient.street,
                        selectedClient.physical_city || selectedClient.city,
                        selectedClient.physical_state || selectedClient.state,
                        selectedClient.physical_zip || selectedClient.zip_code
                      ) || 'Not provided'}
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2">
                      <Mail className="w-4 h-4 flex-shrink-0" />Billing Address
                    </h4>
                    <p className="text-sm break-words">
                      {formatAddress(
                        selectedClient.billing_street,
                        selectedClient.billing_city,
                        selectedClient.billing_state,
                        selectedClient.billing_zip
                      ) || 'Same as physical'}
                    </p>
                    {/* AP Email Address - shown under billing address */}
                    {selectedClient.ar_email_address && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <span className="text-xs font-medium text-slate-500">AP Email:</span>
                        <a href={`mailto:${selectedClient.ar_email_address}`} className="text-sm text-amber-600 hover:underline ml-1 break-all">
                          {selectedClient.ar_email_address}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Website */}
                {selectedClient.website && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                    <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <a href={selectedClient.website.startsWith('http') ? selectedClient.website : `https://${selectedClient.website}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">{selectedClient.website}</a>
                  </div>
                )}

                {/* Contacts */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Contacts ({getContactsCount(selectedClient)})
                    </h3>
                    {canManage && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50"
                        onClick={() => handleCreateContact(selectedClient)}
                        data-testid="create-contact-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Contact
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {getDisplayContacts(selectedClient).length > 0 ? (
                      getDisplayContacts(selectedClient).map((contact, idx) => (
                        <Card key={idx} className="border-slate-200">
                          <CardContent className="p-3 sm:p-4">
                            <div className="flex items-start justify-between">
                              <div className="min-w-0">
                                <p className="font-semibold break-words">{contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}</p>
                                {(contact.title || contact.role) && <Badge variant="outline" className="mt-1">{contact.title || contact.role}</Badge>}
                              </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mt-3">
                              {contact.email && (
                                <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-600 break-all">
                                  <Mail className="w-4 h-4 flex-shrink-0" />{contact.email}
                                </a>
                              )}
                              {(contact.phone || contact.phone1) && (
                                <a href={`tel:${contact.phone || contact.phone1}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-amber-600">
                                  <Phone className="w-4 h-4 flex-shrink-0" />{contact.phone || contact.phone1}
                                </a>
                              )}
                            </div>
                            {/* New Proposal button for this contact - only for users with create_proposals permission */}
                            {canCreateProposals && (
                              <div className="mt-3 pt-3 border-t border-slate-100">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                                  onClick={() => handleNewProposal(selectedClient, contact)}
                                >
                                  <FileText className="w-4 h-4 mr-2" />
                                  New Proposal
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <p className="text-slate-500 text-sm">No contacts added yet</p>
                    )}
                  </div>
                </div>

                {/* Jobs History */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    Jobs History ({clientJobs.length})
                  </h3>
                  {clientJobs.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Job #</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="hidden sm:table-cell">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientJobs.map(job => (
                            <TableRow key={job.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/jobs/${job.id}`)}>
                              <TableCell className="font-mono text-sm">{job.job_number || '-'}</TableCell>
                              <TableCell className="font-medium">{job.job_title}</TableCell>
                              <TableCell><Badge className={getStatusColor(job.status)}>{job.status}</Badge></TableCell>
                              <TableCell className="hidden sm:table-cell">{job.date || job.created_at ? format(new Date(job.date || job.created_at), 'MMM d, yyyy') : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No jobs yet for this client</p>
                  )}
                </div>

                {/* Proposals History */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Proposals ({clientProposals.length})
                  </h3>
                  {clientProposals.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Proposal #</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Total</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="hidden sm:table-cell">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientProposals.map(proposal => (
                            <TableRow key={proposal.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/proposals/${proposal.id}/preview`)}>
                              <TableCell className="font-mono text-sm">{proposal.proposal_number || '-'}</TableCell>
                              <TableCell>{proposal.proposal_type || '-'}</TableCell>
                              <TableCell className="font-medium">${proposal.total?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell><Badge variant="outline">{getStatusLabel(proposal.status)}</Badge></TableCell>
                              <TableCell className="hidden sm:table-cell">{proposal.created_at ? format(new Date(proposal.created_at), 'MMM d, yyyy') : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No proposals yet for this client</p>
                  )}
                </div>

                {/* Opportunities History */}
                <div>
                  <h3 className="text-base sm:text-lg font-semibold mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Opportunities ({clientOpportunities.length})
                  </h3>
                  {clientOpportunities.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Value</TableHead>
                            <TableHead>Stage</TableHead>
                            <TableHead className="hidden sm:table-cell">Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {clientOpportunities.map(opp => (
                            <TableRow key={opp.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate('/opportunities')}>
                              <TableCell className="font-medium">{opp.name || opp.opportunity_name || '-'}</TableCell>
                              <TableCell className="font-medium">${opp.value?.toLocaleString() || opp.estimated_value?.toLocaleString() || '0'}</TableCell>
                              <TableCell><Badge variant="outline">{opp.stage || opp.status || '-'}</Badge></TableCell>
                              <TableCell className="hidden sm:table-cell">{opp.created_at ? format(new Date(opp.created_at), 'MMM d, yyyy') : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No opportunities yet for this client</p>
                  )}
                </div>

                {/* Audit Info */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 text-xs text-slate-400 pt-4 border-t">
                  {selectedClient.entered_by && (
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      Entered by: {selectedClient.entered_by}
                    </div>
                  )}
                  {selectedClient.created_at && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Created: {format(new Date(selectedClient.created_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  )}
                </div>

                {selectedClient.notes && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2">Notes</h3>
                    <p className="text-slate-600 bg-slate-50 p-3 rounded-lg whitespace-pre-wrap">{selectedClient.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-lg sm:text-xl font-bold">
                {selectedClient ? 'Edit Client' : 'Add Client'}
              </DialogTitle>
              {selectedClient && canCreateProposals && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => {
                    setShowDialog(false);
                    handleNewProposal(selectedClient);
                  }}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  New Proposal
                </Button>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4 sm:space-y-6 pt-4">
            {/* Client Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Building className="w-5 h-5" />Client Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name (Company) *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                    className="bg-slate-50"
                    placeholder="Company name"
                    data-testid="client-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Client Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="www.example.com"
                    className="bg-slate-50"
                  />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label>Industry Classification</Label>
                  <select
                    value={formData.industry_classification}
                    onChange={(e) => {
                      setFormData(prev => ({ 
                        ...prev, 
                        industry_classification: e.target.value,
                        custom_industry: e.target.value === 'Other' ? prev.custom_industry : ''
                      }));
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Select classification</option>
                    {industryClassifications.filter(c => c.active && c.name !== 'Other').map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                    <option value="Other">Other (specify below)</option>
                  </select>
                  {formData.industry_classification === 'Other' && (
                    <input
                      type="text"
                      value={formData.custom_industry || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_industry: e.target.value }))}
                      placeholder="Describe the industry..."
                      className="flex h-10 w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm mt-2"
                    />
                  )}
                  {isAdmin() && (
                    <p className="text-xs text-slate-400">Manage classifications in Services menu</p>
                  )}
                </div>
              </div>
            </div>

            {/* Physical Address */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5" />Physical Address
              </h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Street</Label>
                  <Input 
                    value={formData.physical_street} 
                    onChange={(e) => setFormData(prev => ({ ...prev, physical_street: e.target.value }))} 
                    className="bg-slate-50" 
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                  <div className="col-span-2 sm:col-span-3 space-y-2">
                    <Label className="text-xs text-slate-500">City</Label>
                    <Input value={formData.physical_city} onChange={(e) => setFormData(prev => ({ ...prev, physical_city: e.target.value }))} className="bg-slate-50" />
                  </div>
                  <div className="col-span-1 sm:col-span-2 space-y-2">
                    <Label className="text-xs text-slate-500">State</Label>
                    <select 
                      value={formData.physical_state} 
                      onChange={(e) => setFormData(prev => ({ ...prev, physical_state: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 space-y-2">
                    <Label className="text-xs text-slate-500">ZIP</Label>
                    <Input value={formData.physical_zip} onChange={(e) => setFormData(prev => ({ ...prev, physical_zip: e.target.value }))} className="bg-slate-50" />
                  </div>
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Mail className="w-5 h-5" />Billing Address
                </h3>
                <Button variant="outline" size="sm" onClick={copyBillingFromPhysical} type="button">
                  <Copy className="w-4 h-4 mr-1" />Copy from Physical
                </Button>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Street</Label>
                  <Input 
                    value={formData.billing_street} 
                    onChange={(e) => setFormData(prev => ({ ...prev, billing_street: e.target.value }))} 
                    className="bg-slate-50" 
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
                  <div className="col-span-2 sm:col-span-3 space-y-2">
                    <Label className="text-xs text-slate-500">City</Label>
                    <Input value={formData.billing_city} onChange={(e) => setFormData(prev => ({ ...prev, billing_city: e.target.value }))} className="bg-slate-50" />
                  </div>
                  <div className="col-span-1 sm:col-span-2 space-y-2">
                    <Label className="text-xs text-slate-500">State</Label>
                    <select 
                      value={formData.billing_state} 
                      onChange={(e) => setFormData(prev => ({ ...prev, billing_state: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-slate-50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select state</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 space-y-2">
                    <Label className="text-xs text-slate-500">ZIP</Label>
                    <Input value={formData.billing_zip} onChange={(e) => setFormData(prev => ({ ...prev, billing_zip: e.target.value }))} className="bg-slate-50" />
                  </div>
                </div>
                {/* AP Email Address */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">AP Email Address</Label>
                  <Input 
                    type="email"
                    value={formData.ar_email_address} 
                    onChange={(e) => setFormData(prev => ({ ...prev, ar_email_address: e.target.value }))} 
                    className="bg-slate-50" 
                    placeholder="ap@company.com"
                    data-testid="ar-email-address-input"
                  />
                  <p className="text-xs text-slate-400">Email address for Accounts Payable communications</p>
                </div>
              </div>
            </div>

            {/* Contacts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-5 h-5" />Contacts ({formData.contacts.length})
                </h3>
                <Button variant="outline" size="sm" onClick={handleAddContact} type="button" className="bg-amber-50 border-amber-300 hover:bg-amber-100">
                  <Plus className="w-4 h-4 mr-1" />Add Contact
                </Button>
              </div>

              {formData.contacts.map((contact, idx) => (
                <Card key={idx} className="border-slate-200 border-l-4 border-l-amber-500">
                  <CardContent className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-amber-600">Contact #{idx + 1}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveContact(idx)} className="text-red-600 h-8 w-8 p-0" type="button">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">First Name *</Label>
                        <Input 
                          value={contact.first_name || contact.name?.split(' ')[0] || ''} 
                          onChange={(e) => handleContactChange(idx, 'first_name', e.target.value)} 
                          className="h-10 bg-slate-50" 
                          placeholder="First name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Last Name *</Label>
                        <Input 
                          value={contact.last_name || contact.name?.split(' ').slice(1).join(' ') || ''} 
                          onChange={(e) => handleContactChange(idx, 'last_name', e.target.value)} 
                          className="h-10 bg-slate-50" 
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Title / Role</Label>
                        <Input 
                          value={contact.title || contact.role || ''} 
                          onChange={(e) => handleContactChange(idx, 'title', e.target.value)} 
                          className="h-10 bg-slate-50" 
                          placeholder="e.g., Owner, Manager, PM"
                          list={`title-options-${idx}`}
                        />
                        <datalist id={`title-options-${idx}`}>
                          {TITLE_OPTIONS.map(t => <option key={t} value={t} />)}
                        </datalist>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Email</Label>
                        <Input 
                          type="email" 
                          value={contact.email || ''} 
                          onChange={(e) => handleContactChange(idx, 'email', e.target.value)} 
                          className="h-10 bg-slate-50" 
                          placeholder="email@example.com"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Phone</Label>
                      <Input 
                        type="tel" 
                        value={contact.phone || contact.phone1 || ''} 
                        onChange={(e) => handleContactChange(idx, 'phone', e.target.value)} 
                        className="h-10 bg-slate-50" 
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}

              {formData.contacts.length === 0 && (
                <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <User className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-500 text-sm">No contacts added yet</p>
                  <Button variant="link" onClick={handleAddContact} type="button" className="mt-2 text-amber-600">
                    <Plus className="w-4 h-4 mr-1" />Add your first contact
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="bg-slate-50 min-h-[100px] resize-y"
                placeholder="Additional notes about this client..."
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowDialog(false)} type="button">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-400 text-slate-900" data-testid="save-client-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {selectedClient ? 'Update Client' : 'Create Client'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedClient?.company_name || selectedClient?.client_name}? This will remove all associated contacts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={saving}>
              {saving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-amber-500" />
              Import Contacts from CSV
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Instructions */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-semibold text-slate-900 mb-2">CSV Format</h4>
              <p className="text-sm text-slate-600 mb-3">
                Your CSV should include these columns (order doesn't matter):
              </p>
              <div className="text-xs text-slate-500 space-y-1 font-mono bg-white p-2 rounded border">
                <div>Company Name, Website, Industry</div>
                <div>Physical Address, Billing Address</div>
                <div>City, State, Zip, Country</div>
                <div>Contact 1 Name, Title, Email, Phone</div>
                <div>Contact 2 Name, Title, Email, Phone</div>
                <div>Notes</div>
              </div>
            </div>

            {/* Download Template */}
            <Button 
              variant="outline" 
              className="w-full"
              onClick={downloadTemplate}
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV Template
            </Button>

            {/* File Upload */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-amber-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="csv-upload"
              />
              <label htmlFor="csv-upload" className="cursor-pointer">
                {importing ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                    <span className="text-sm text-slate-600">Importing contacts...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      Click to select CSV file
                    </span>
                    <span className="text-xs text-slate-400">
                      or drag and drop
                    </span>
                  </div>
                )}
              </label>
            </div>

            {/* Import Results */}
            {importResult && (
              <div className={`p-4 rounded-lg border ${
                importResult.imported > 0 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}>
                <div className="flex items-start gap-3">
                  {importResult.imported > 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`font-semibold ${
                      importResult.imported > 0 ? 'text-green-800' : 'text-amber-800'
                    }`}>
                      {importResult.message || (importResult.error ? 'Import Failed' : 'Import Complete')}
                    </p>
                    {importResult.imported > 0 && (
                      <p className="text-sm text-green-700 mt-1">
                        {importResult.imported} contacts imported successfully
                      </p>
                    )}
                    {importResult.skipped > 0 && (
                      <p className="text-sm text-amber-700 mt-1">
                        {importResult.skipped} rows skipped (duplicates or errors)
                      </p>
                    )}
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="mt-2 text-xs text-slate-600 max-h-32 overflow-y-auto">
                        <p className="font-medium mb-1">Details:</p>
                        {importResult.errors.map((err, idx) => (
                          <p key={idx} className="text-red-600">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              {importResult?.imported > 0 ? 'Done' : 'Cancel'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cleanup Duplicate Contacts Dialog */}
      <Dialog open={showCleanupDialog} onOpenChange={setShowCleanupDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-500" />
              Fix Duplicate Contacts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-medium">What this does:</p>
                  <p className="mt-1">
                    This tool removes duplicate contact entries that may be stored in two places. 
                    Your contacts in the "People" section will be preserved - only the duplicates will be removed.
                  </p>
                </div>
              </div>
            </div>

            {!cleanupResult && (
              <div className="text-center py-4">
                <p className="text-slate-600 mb-4">
                  Click "Preview" to see what duplicates will be cleaned up.
                </p>
                <Button 
                  onClick={runCleanupDryRun} 
                  disabled={runningCleanup}
                  variant="outline"
                >
                  {runningCleanup ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4 mr-2" />
                  )}
                  Preview Cleanup
                </Button>
              </div>
            )}

            {cleanupResult && (
              <div className="space-y-3">
                <div className={`p-4 rounded-lg border ${
                  cleanupResult.companies_to_clean?.length > 0 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  {cleanupResult.companies_to_clean?.length > 0 ? (
                    <>
                      <p className="font-medium text-amber-800">
                        Found {cleanupResult.companies_to_clean.length} companies with duplicate contacts
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        Total duplicates to remove: {cleanupResult.total_nested_contacts_to_remove}
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <p className="font-medium text-green-800">
                        {cleanupResult.dry_run 
                          ? 'No duplicate contacts found! Your data is clean.' 
                          : cleanupResult.message}
                      </p>
                    </div>
                  )}
                </div>

                {cleanupResult.companies_to_clean?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Company</TableHead>
                          <TableHead className="text-center">Duplicates</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cleanupResult.companies_to_clean.map((company, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{company.company_name}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className="bg-red-50 text-red-700">
                                {company.nested_contacts_count}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {cleanupResult.dry_run && cleanupResult.companies_to_clean?.length > 0 && (
                  <div className="flex justify-center pt-2">
                    <Button 
                      onClick={runCleanupActual} 
                      disabled={runningCleanup}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      {runningCleanup ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Wrench className="w-4 h-4 mr-2" />
                      )}
                      Remove Duplicates Now
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <Button variant="outline" onClick={() => setShowCleanupDialog(false)}>
              {cleanupResult && !cleanupResult.dry_run ? 'Done' : 'Cancel'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
